import styled from "@emotion/styled"
import { TextField } from "@mui/material"
import { DonationMessages } from "app/[locale]/(layout)/donation-application-form/page"
import { Controller } from "react-hook-form"
import { NewText, useIsDesktop } from "ui"
import CustomHelperText from "./form/CustomHelperText"
import NonBinaryRadio from "./form/NonBinaryRadio"
import RadioButtonController from "./form/RadioButtonController"

const StyledContainer = styled.div`
  input {
    font-size: 16p !important;
  }
  textarea {
    font-size: 16px !important;
  }
  label {
    font-size: 16px;
  }
`
const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    width: 100%;
  }
`
const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 90%;
  }
`
const InputLengthContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: 4px;
`

const StyledButton = styled.button`
  background-color: white;
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 200px;
  border: 2px solid #000;
  outline: none;
  margin-left: auto;
  min-width: 121px;
  margin-top: 24px;
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  &:disabled {
    border: 2px solid #6f6f6f;
    color: #6f6f6f;
  }
`

const StyledMandatoryHead = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

type Props = {
  control: any
  errors: any
  watch: any
  setAtiveAcordion: any
  trigger: any
  messages: DonationMessages
}

const StepA: React.FC<Props> = ({
  control,
  errors,
  watch,
  setAtiveAcordion,
  trigger,
  messages,
}) => {
  const handleContinue = (e: any) => {
    e.preventDefault()

    trigger([
      "PartAFromNonProfitOrganization",
      "PartANotMarketingPromotion",
      "PartASubject",
      "PartAAssociationDetailsName",
      "PartAAssociationDetailsNumber",
      "PartAAssociationDetailsPhone",
      "PartAAssociationDetailsWebsite",
      "PartAAssociationDetailsCeo",
      "PartAAmount",
      "PartAVolunteeringOptions",
      "PartAContactUsDetailsName",
      "PartAContactUsDetailsContactRole",
      "PartAContactUsDetailsPhone",
      "PartAContactUsDetailsEmail",
      "PartAActivityAndGoals",
      "PartAIsAshtromGroupConnection",
      "PartAAshtromGroupConnection",
    ]).then((res: any) => {
      if (res) {
        setAtiveAcordion(4)
        document
          // eslint-disable-next-line quotes
          .querySelector('#panel1a-header[aria-expanded="true"]')
          ?.scrollIntoView({ behavior: "smooth" })
      } else {
        setTimeout(() => {
          const firstError = document.querySelector<HTMLElement>(
            "[aria-invalid='true']",
          )
          firstError?.focus()
          firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
        }, 100)
      }
    })
  }
  const isDesktop = useIsDesktop()

  return (
    <StyledContainer>
      <NewText
        id="PartAFromNonProfitOrganization-heading"
        desktopFontSize={18}
        desktopFontWeight="400"
        desktopLineHeight="26px"
        mobileFontSize={16}
        mobileFontWeight="400"
        mobileLineHeight={"28px"}
        component="p"
        color={"black"}
        style={{ width: "100%" }}
      >
        {messages.isTheAppli}
      </NewText>
      <RadioButtonController
        no={messages.no}
        yes={messages.yes}
        title={messages.forClarif}
        control={control}
        name="PartAFromNonProfitOrganization"
        desktopFontSize={14}
        mobileFontSize={12}
        style={{ marginTop: 0 }}
        error={!!errors.PartAFromNonProfitOrganization}
        errorMessage={messages.selectionRequired}
        externalHeadingId="PartAFromNonProfitOrganization-heading"
      />
      <RadioButtonController
        no={messages.no}
        yes={messages.yes}
        title={messages.thisApplic}
        control={control}
        name="PartANotMarketingPromotion"
        error={!!errors.PartANotMarketingPromotion}
        errorMessage={messages.selectionRequired}
      />
      <NonBinaryRadio
        messages={messages}
        title={messages.isTheAppl1}
        control={control}
        name="PartASubject"
        error={!!errors.PartASubject}
        errorMessage={messages.selectionRequired}
      />
      <div role="group" aria-labelledby="association-details-heading">
        <StyledMandatoryHead>
          <NewText
            id="association-details-heading"
            desktopFontSize={18}
            desktopFontWeight="400"
            desktopLineHeight="26px"
            mobileFontSize={16}
            mobileFontWeight="400"
            mobileLineHeight={"28px"}
            component="p"
            color={"black"}
            style={{ maxWidth: "560px" }}
          >
            {messages.associati1}
          </NewText>
          <NewText
            desktopFontSize={16}
            desktopFontWeight="400"
            desktopLineHeight="23px"
            mobileFontSize={14}
            mobileFontWeight="400"
            mobileLineHeight={"20px"}
            component="p"
            color={"black"}
            style={{ maxWidth: "560px" }}
          >
            {messages.mandatoryF}
          </NewText>
        </StyledMandatoryHead>
        <StyledInputContainer style={{ width: isDesktop ? "66%" : "100%" }}>
          <Controller
            name="PartAAssociationDetailsName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                fullWidth
                label={messages.nameOfTheA}
                type="text"
                variant="filled"
                error={!!errors.PartAAssociationDetailsName}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAAssociationDetailsName}
                    text={messages.theNameOfT}
                  />
                }
                sx={{
                  marginBottom: "37px",
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
          <Controller
            name="PartAAssociationDetailsNumber"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                fullWidth
                label={messages.registrati}
                type="text"
                variant="filled"
                error={!!errors.PartAAssociationDetailsNumber}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAAssociationDetailsNumber}
                    text={messages.registered}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <Controller
            name="PartAAssociationDetailsPhone"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.phone}
                fullWidth
                type="text"
                variant="filled"
                error={!!errors.PartAAssociationDetailsPhone}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAAssociationDetailsPhone}
                    text={messages.thePhoneNu}
                  />
                }
                sx={{
                  marginBottom: "37px",
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
          <Controller
            name="PartAAssociationDetailsWebsite"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.website}
                fullWidth
                type="text"
                variant="filled"
                error={!!errors.PartAAssociationDetailsWebsite}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAAssociationDetailsWebsite}
                    text={messages.theWebsite}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
          <Controller
            name="PartAAssociationDetailsCeo"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                fullWidth
                label={messages.ceoDetails}
                type="text"
                variant="filled"
                error={!!errors.PartAAssociationDetailsCeo}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAAssociationDetailsCeo}
                    text={messages.theDetails}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
        </StyledInputContainer>
      </div>
      <div role="group" aria-labelledby="amount-heading">
        <NewText
          id="amount-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="26px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginBottom: "8px" }}
        >
          {messages.pleaseIndi}
        </NewText>
        <Controller
          name="PartAAmount"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              fullWidth={isDesktop ? false : true}
              label={messages.enterAmoun}
              type="text"
              variant="filled"
              error={!!errors.PartAAmount}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartAAmount}
                  text={messages.theAmountE}
                />
              }
              sx={{
                marginBottom: "37px",
              }}
              inputProps={{
                style: {
                  fontSize: "16px",
                },
              }}
            />
          )}
        />
      </div>
      <div role="group" aria-labelledby="volunteering-heading">
        <NewText
          id="volunteering-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="26px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px" }}
        >
          {messages.areThereVo}
        </NewText>
        <NewText
          desktopFontSize={14}
          desktopFontWeight="400"
          desktopLineHeight="20px"
          mobileFontSize={12}
          mobileFontWeight="400"
          mobileLineHeight={"17px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "800px", marginBottom: "16px" }}
        >
          {messages.ifYesPlea}
        </NewText>
        <TextAreaContainer>
          <Controller
            name="PartAVolunteeringOptions"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.descriptio}
                type="text"
                variant="filled"
                error={!!errors.PartAVolunteeringOptions}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAVolunteeringOptions}
                    text={messages.theDescrip}
                  />
                }
                fullWidth
                multiline
                rows={6}
                inputProps={{
                  "aria-required": "true",
                  maxLength: 500,
                  style: {
                    resize: "vertical",
                    fontSize: "16px",
                  },
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    right: 0,
                    bottom: "-22px",
                  },
                  "& .MuiInputLabel-root": {
                    top: "-5px",
                  },
                  width: "100%",
                }}
              />
            )}
          />
          <InputLengthContainer>
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontSize={12}
              mobileFontWeight="400"
              mobileLineHeight={"17.28px"}
              component="p"
              color={"black"}
            >
              {watch("PartAVolunteeringOptions")?.length || 0}/500
            </NewText>
          </InputLengthContainer>
        </TextAreaContainer>
      </div>
      <div role="group" aria-labelledby="contact-details-heading">
        <NewText
          id="contact-details-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "24px", marginBottom: "16px" }}
        >
          {messages.contactDet}
        </NewText>
        <StyledInputContainer>
          <Controller
            name="PartAContactUsDetailsName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.contactNam}
                type="text"
                variant="filled"
                error={!!errors.PartAContactUsDetailsName}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAContactUsDetailsName}
                    text={messages.contactNa1}
                  />
                }
                sx={{
                  marginBottom: "37px",
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
          <Controller
            name="PartAContactUsDetailsContactRole"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.contactPer}
                type="text"
                variant="filled"
                error={!!errors.PartAContactUsDetailsContactRole}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAContactUsDetailsContactRole}
                    text={messages.contactPe1}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
          <Controller
            name="PartAContactUsDetailsPhone"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                label={messages.contactPho}
                type="text"
                variant="filled"
                error={!!errors.PartAContactUsDetailsPhone}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartAContactUsDetailsPhone}
                    text={messages.thePhoneNu}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
                inputProps={{
                  "aria-required": "true",
                  style: {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          />
        </StyledInputContainer>
        <Controller
          name="PartAContactUsDetailsEmail"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              label={messages.contactEmail}
              type="text"
              variant="filled"
              fullWidth={isDesktop ? false : true}
              error={!!errors.PartAContactUsDetailsEmail}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartAContactUsDetailsEmail}
                  text={messages.contactEmailError}
                />
              }
              sx={{
                marginBottom: "37px",
              }}
              inputProps={{
                style: {
                  fontSize: "16px",
                },
              }}
            />
          )}
        />
      </div>
      <TextAreaContainer>
        <Controller
          name="PartAActivityAndGoals"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              label={messages.associatio}
              type="text"
              variant="filled"
              error={!!errors.PartAActivityAndGoals}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartAActivityAndGoals}
                  text={messages.associationError}
                />
              }
              fullWidth
              multiline
              rows={6}
              inputProps={{
                maxLength: 500,
                style: {
                  resize: "vertical",
                  fontSize: "16px",
                },
              }}
              sx={{
                "& .MuiFormHelperText-root": {
                  right: 0,
                  bottom: "-22px",
                },
                "& .MuiInputLabel-root": {
                  top: "-5px",
                },
              }}
            />
          )}
        />
        <InputLengthContainer>
          <NewText
            desktopFontSize={14}
            desktopFontWeight="400"
            desktopLineHeight="20px"
            mobileFontSize={12}
            mobileFontWeight="400"
            mobileLineHeight={"17.28px"}
            component="p"
            color={"black"}
          >
            {watch("PartAActivityAndGoals")?.length || 0}/500
          </NewText>
        </InputLengthContainer>
      </TextAreaContainer>
      <RadioButtonController
        no={messages.no}
        yes={messages.yes}
        title={messages.doesTheApp}
        control={control}
        name="PartAIsAshtromGroupConnection"
        error={!!errors.PartAIsAshtromGroupConnection}
        errorMessage={messages.selectionRequired}
      />
      <TextAreaContainer>
        <Controller
          name="PartAAshtromGroupConnection"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              label={messages.ifYesPle1}
              type="text"
              variant="filled"
              error={!!errors.PartAAshtromGroupConnection}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartAAshtromGroupConnection}
                  text={messages.ifYesError}
                />
              }
              fullWidth
              multiline
              rows={6}
              inputProps={{
                maxLength: 500,
                style: {
                  resize: "vertical",
                  fontSize: "16px",
                },
              }}
              sx={{
                "& .MuiFormHelperText-root": {
                  right: 0,
                  bottom: "-22px",
                },
                "& .MuiInputLabel-root": {
                  top: "-5px",
                },
              }}
            />
          )}
        />
        <InputLengthContainer>
          <NewText
            desktopFontSize={14}
            desktopFontWeight="400"
            desktopLineHeight="20px"
            mobileFontSize={12}
            mobileFontWeight="400"
            mobileLineHeight={"17.28px"}
            component="p"
            color={"black"}
          >
            {watch("PartAAshtromGroupConnection")?.length || 0}/500
          </NewText>
        </InputLengthContainer>
      </TextAreaContainer>
      <StyledButton onClick={handleContinue}>
        <NewText
          desktopFontSize={16}
          desktopFontWeight="600"
          desktopLineHeight="23px"
          mobileFontSize={14}
          mobileFontWeight="600"
          mobileLineHeight={"20px"}
          component="p"
          color={"black"}
          marginRight="4px"
          maxWidth={"80%"}
        >
          {messages.toStep2}
        </NewText>
      </StyledButton>
    </StyledContainer>
  )
}

export default StepA
